import { AfterViewInit, Component, inject, Input, ChangeDetectionStrategy } from '@angular/core';
import { TestToolService } from '../services/test-tool.service';
import { CurrentUserService } from '../../user/services';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import { JsonHttpService } from '../../shared/http/json-http.service';
import { DocumentEncoding } from '../../distribution/models';
import { DistributionService } from '../../distribution/services/distribution.service';
import { GridService } from '../../shared/grid/grid.service';
import { TestFileExpectedOutputDto } from '../models/test-file-expected-output-dto';
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { TestFileDto, TestFileType } from '../models/test-file-dto';
import { DxDataGridModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoRowDraggingModule, DxoSortingModule, DxoScrollingModule, DxoColumnFixingModule, DxoEditingModule, DxoFormModule, DxiItemModule, DxoTextsModule, DxiColumnModule, DxiValidationRuleModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';
import { FileUploaderComponent } from '../../shared/upload/file-uploader.component';
import { EncodingPickerComponent } from '../../shared/encoding/encoding-picker.component';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';
import { DeveloperService } from 'src/app/developer/services/developer.service';
import { ContentType } from 'src/app/distribution/models/content-type';
import { EncodingPickerService } from 'src/app/shared/encoding/encoding-picker.service';

@Component({
    selector: 'app-test-tool-test-case-interchange-expected-output-files',
    templateUrl: './test-tool-test-case-interchange-expected-output-files.component.html',
    styleUrls: ['./test-tool-test-case-interchange-expected-output-files.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DxDataGridModule, BizDataGridDirective, DxoRowDraggingModule, DxoSortingModule, DxoScrollingModule, DxoColumnFixingModule, DxoEditingModule, DxoFormModule, DxiItemModule, DxoTextsModule, DxiColumnModule, DxiValidationRuleModule, BizUserColumnDirective, BizDateColumnDirective, DxTemplateModule, FileUploaderComponent, EncodingPickerComponent]
})
export class TestToolTestCaseInterchangeExpectedOutputFilesComponent implements AfterViewInit {

    public bizGridFiltervalues = inject(BizGridFiltervalues);

    private developerService = inject(DeveloperService);
    private testToolService = inject(TestToolService);
    private currentUserService = inject(CurrentUserService);
    private toastService = inject(ToastService);
    private jsonHttpService = inject(JsonHttpService);
    private distributionService = inject(DistributionService);
    private gridService = inject(GridService);
    private encodingPickerService = inject(EncodingPickerService);
    
    allowAdding = false;
    allowUpdating = false;
    allowDeleting = false;
    expectedOutputTestFiles: TestFileExpectedOutputDto[] = [];
    currentUploadedInterchangeOutDataGuid: string | null;
    encodings: DocumentEncoding[] = [];
    contentTypes: ContentType[] = [];
    
    @Input() testCaseInterchangeId: number;

    ngAfterViewInit() {
        void this.refresh();
    }

    async refresh()
    {
        const [expectedOutputTestFiles, encodings, contentTypes] = await Promise.all([
            this.testToolService.getTestCaseInterchangeExpectedOutputFiles(this.testCaseInterchangeId),
            this.distributionService.getDocumentEncodings(),
            this.developerService.getContentTypes()
        ]);

        this.expectedOutputTestFiles = expectedOutputTestFiles;
        this.encodings = encodings.sort((a, b) => a.DisplayName.localeCompare(b.DisplayName));
        this.contentTypes = contentTypes.sort((a, b) => a.Name.localeCompare(b.Name));

        this.allowAdding = this.currentUserService.hasPermission('TestTool_Action_TestCase_Create');
        this.allowUpdating = this.currentUserService.hasPermission('TestTool_Action_TestCase_Edit');
        this.allowDeleting = this.currentUserService.hasPermission('TestTool_Action_TestCase_Delete');
    }

    onReorder = (e: Parameters<DxDataGridTypes.RowDragging['onReorder']>[0]) => {
        this.expectedOutputTestFiles.splice(e.fromIndex, 1);
        this.expectedOutputTestFiles.splice(e.toIndex, 0, e.itemData);

        this.expectedOutputTestFiles.map((x, index) => {
            x.SortOrder = index; // Set sort-order...
        });

        this.testToolService.saveTestFileExpectedOutputs(this.expectedOutputTestFiles).then((result) => { this.expectedOutputTestFiles = result; });
    };

    fileOnValueChangedCallback(args: any, cellInfo: any) {
        if (args.value.length > 0) {
            cellInfo.setValue(args.value[0].name);
        }
        else {
            cellInfo.setValue(null);
        }
    }

    fileOnBeforeSendCallback(args: any, cellInfo: any): void {
        const request = args.request as XMLHttpRequest;
        const headers = this.jsonHttpService.generateHeaders();
        request.setRequestHeader('Authorization', headers.get('Authorization'));
    }

    fileOnUploadedCallback(args: any, cellInfo: any) {
        if (args.request.status === 200) {
            const uploadGuid = JSON.parse(args.request.responseText);
            this.currentUploadedInterchangeOutDataGuid = uploadGuid;
        }
    }

    setEncodingId(encodingId: number, cellInfo: any) {
        cellInfo.setValue(encodingId || null);
    }

    onInitNewRow(e: any): void { 
        e.data = {
            Id: 0,
            TestCaseInterchangeId: this.testCaseInterchangeId,
            File: {
                Id: 0,
                FileType: TestFileType.TypeExpectedInterchangeOut,
                ContentTypeId: null,
                EncodingId: null,
                Filename: null,
                Note: null
            } as TestFileDto,
            SortOrder: this.expectedOutputTestFiles.length
        } as TestFileExpectedOutputDto;
    }

    onRowInserting = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as TestFileExpectedOutputDto, item => this.saveTestFileExpectedOutputDto(item));
    };

    onRowUpdating = (e: any): void => {
        // The spread operator only does a shallow copy, so TestFileExpectedOutputDto.File will only be e.newData and therefore we would lose data if we don't do this in two steps.
        let testFileExpectedOutputDto = { ...e.key, ...e.newData } as TestFileExpectedOutputDto;
        let testFileDto = { ...e.key.File, ...e.newData.File } as TestFileDto
        testFileExpectedOutputDto.File = testFileDto;
        e.cancel = this.gridService.handleDataGridRow(testFileExpectedOutputDto, item => this.saveTestFileExpectedOutputDto(item), e.newData);
    };

    onRowRemoving = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as TestFileExpectedOutputDto, item => this.deleteTestFileExpectedOutputDto(item));
    };

    onEditCanceled(e: any): void {
        this.currentUploadedInterchangeOutDataGuid = null;
    }

    encodingDisplayExpr = (item?: DocumentEncoding) =>
        this.encodingPickerService.formatEncodingDisplay(item);

    async saveTestFileExpectedOutputDto(testFileExpectedOutputDto: TestFileExpectedOutputDto): Promise<TestFileExpectedOutputDto> {
        testFileExpectedOutputDto.File.UploadFileGuid = this.currentUploadedInterchangeOutDataGuid;
        const newTestFileExpectedOutputDto = await this.testToolService.saveTestFileExpectedOutput(testFileExpectedOutputDto);
        this.toastService.showToast('Expected output file saved...', ToastType.success);
        this.currentUploadedInterchangeOutDataGuid = null;
        return newTestFileExpectedOutputDto;
    }

    async deleteTestFileExpectedOutputDto(testFileExpectedOutputDto: TestFileExpectedOutputDto): Promise<TestFileExpectedOutputDto> {
        await this.testToolService.deleteTestCaseInterchangeExpectedOuputFile(testFileExpectedOutputDto.Id);
        this.toastService.showToast('Expected output file deleted...', ToastType.success);
        return testFileExpectedOutputDto;
    }
}
